import { useEffect, useState } from "react";
import { api, openAuthedBlob } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Save, X, ExternalLink, FileText, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact inline invoice editor used inside expandable bulk rows.
 * Fetches the invoice, shows line items, allows editing description / qty / rate / hsn.
 */
export default function InlineInvoiceEditor({ invoiceId, onUpdated }) {
  const [inv, setInv] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoiceId) return;
    api.get(`/invoices/${invoiceId}`).then(({ data }) => setInv(data)).catch(() => {
      toast.error("Failed to load invoice");
    });
  }, [invoiceId]);

  if (!inv) return <div className="p-4 text-xs text-zinc-500" data-testid="inline-editor-loading">Loading invoice…</div>;

  const items = editMode ? draft.line_items : inv.line_items;
  const intra = inv.intra_state;

  const startEdit = () => {
    setDraft({
      ...inv,
      line_items: (inv.line_items || []).map((i) => ({ ...i })),
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditMode(false);
  };

  const updateItem = (idx, key, value) => {
    setDraft((d) => {
      const li = d.line_items.map((x, i) => (i === idx ? { ...x, [key]: value } : x));
      return { ...d, line_items: li };
    });
  };

  const removeItem = (idx) => {
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.filter((_, i) => i !== idx),
    }));
  };

  const addItem = () => {
    setDraft((d) => {
      const lastGst = d.line_items.length ? Number(d.line_items[d.line_items.length - 1].gst_rate) || 18 : 18;
      return {
        ...d,
        line_items: [
          ...d.line_items,
          { description: "", hsn: "", quantity: 1, unit: "Pcs", unit_price: 0, gst_rate: lastGst },
        ],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        line_items: draft.line_items.map((i) => ({
          ...i,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          gst_rate: Number(i.gst_rate) || 0,
        })),
      };
      const { data } = await api.put(`/invoices/${invoiceId}`, payload);
      setInv(data);
      setEditMode(false);
      setDraft(null);
      toast.success(`Invoice ${data.invoice_number} updated`);
      onUpdated?.(data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const computeTotals = () => {
    let subtotal = 0, tax = 0;
    items.forEach((i) => {
      const q = Number(i.quantity) || 0;
      const r = Number(i.unit_price) || 0;
      const g = Number(i.gst_rate) || 0;
      subtotal += q * r;
      tax += (q * r * g) / 100;
    });
    return { subtotal, tax, total: subtotal + tax };
  };
  const t = editMode ? computeTotals() : {
    subtotal: inv.subtotal,
    tax: inv.igst_total || inv.cgst_total + inv.sgst_total,
    total: inv.grand_total,
  };

  return (
    <div className="bg-zinc-50 border-t border-zinc-200 p-4" data-testid={`inline-editor-${invoiceId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs">
          <span className="font-semibold mono">{inv.invoice_number}</span>
          <span className="text-zinc-500 ml-2">·</span>
          <span className="text-zinc-600 ml-2">{inv.buyer?.name}</span>
          <span className="text-zinc-500 ml-2">·</span>
          <span className="text-zinc-600 ml-2">{inv.invoice_date}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-7" asChild data-testid={`open-full-${invoiceId}`}>
            <a href={`/invoice/${invoiceId}`} target="_blank" rel="noreferrer">
              <ExternalLink size={12} className="mr-1" /> Full view
            </a>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={async () => {
              try {
                await openAuthedBlob(`/invoices/${invoiceId}/pdf`);
              } catch {
                toast.error("Failed to download PDF");
              }
            }}
            data-testid={`download-pdf-inline-${invoiceId}`}
          >
            <FileText size={12} className="mr-1" /> PDF
          </Button>
          {editMode ? (
            <>
              <Button size="sm" variant="outline" className="h-7" onClick={cancelEdit} data-testid={`inline-cancel-${invoiceId}`}>
                <X size={12} className="mr-1" /> Cancel
              </Button>
              <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving} data-testid={`inline-save-${invoiceId}`}>
                <Save size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" className="h-7" onClick={startEdit} data-testid={`inline-edit-${invoiceId}`}>
              <Pencil size={12} className="mr-1" /> Edit
            </Button>
          )}
        </div>
      </div>

      <table className="w-full text-xs border border-zinc-200 bg-white table-fixed">
        <thead className="bg-zinc-100 text-zinc-500">
          <tr>
            <th className="p-1.5 text-left w-8">#</th>
            <th className="p-1.5 text-left">Description</th>
            <th className="p-1.5 w-32">HSN</th>
            <th className="p-1.5 w-24 text-right">Qty</th>
            <th className="p-1.5 w-32 text-right">Rate</th>
            <th className="p-1.5 w-20 text-right">GST %</th>
            <th className="p-1.5 w-32 text-right">Amount</th>
            {editMode && <th className="p-1.5 w-8"></th>}
          </tr>
        </thead>
        <tbody className="mono tabular">
          {items.map((it, i) => {
            const q = Number(it.quantity) || 0;
            const r = Number(it.unit_price) || 0;
            const g = Number(it.gst_rate) || 0;
            const amt = q * r * (1 + g / 100);
            return (
              <tr key={i} className="border-t border-zinc-100" data-testid={`inline-row-${invoiceId}-${i}`}>
                <td className="p-1.5 text-zinc-500 text-center align-top">{i + 1}</td>
                <td className="p-1.5 font-sans align-top break-words whitespace-normal">
                  {editMode ? (
                    <Textarea
                      value={it.description}
                      onChange={(e) => updateItem(i, "description", e.target.value)}
                      className="text-xs px-2 py-1 min-h-[36px] leading-snug"
                      data-testid={`inline-desc-${invoiceId}-${i}`}
                    />
                  ) : (
                    it.description
                  )}
                </td>
                <td className="p-1.5 align-top break-words">
                  {editMode ? (
                    <Input
                      value={it.hsn}
                      onChange={(e) => updateItem(i, "hsn", e.target.value)}
                      className="h-7 text-xs mono px-1"
                      data-testid={`inline-hsn-${invoiceId}-${i}`}
                    />
                  ) : (
                    it.hsn
                  )}
                </td>
                <td className="p-1.5 text-right align-top">
                  {editMode ? (
                    <Input
                      type="number"
                      value={it.quantity}
                      onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      className="h-7 text-xs mono text-right px-1"
                      data-testid={`inline-qty-${invoiceId}-${i}`}
                    />
                  ) : (
                    q
                  )}
                </td>
                <td className="p-1.5 text-right align-top">
                  {editMode ? (
                    <Input
                      type="number"
                      step="0.01"
                      value={it.unit_price}
                      onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                      className="h-7 text-xs mono text-right px-1"
                      data-testid={`inline-rate-${invoiceId}-${i}`}
                    />
                  ) : (
                    r.toFixed(2)
                  )}
                </td>
                <td className="p-1.5 text-right align-top">
                  {editMode ? (
                    <Input
                      type="number"
                      value={it.gst_rate}
                      onChange={(e) => updateItem(i, "gst_rate", e.target.value)}
                      className="h-7 text-xs mono text-right px-1"
                      data-testid={`inline-gst-${invoiceId}-${i}`}
                    />
                  ) : (
                    `${g}%`
                  )}
                </td>
                <td className="p-1.5 text-right font-semibold">
                  {amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                {editMode && (
                  <td className="p-1.5 text-center">
                    <button
                      className="text-zinc-400 hover:text-red-600"
                      onClick={() => removeItem(i)}
                      title="Remove this line item"
                      data-testid={`inline-remove-${invoiceId}-${i}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot className="mono tabular">
          <tr className="border-t border-zinc-200 bg-zinc-50">
            <td colSpan={editMode ? 7 : 6} className="p-1.5 text-right text-zinc-500 uppercase text-[10px] tracking-wider">
              Subtotal
            </td>
            <td className="p-1.5 text-right">₹ {t.subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr className="border-t border-zinc-100 bg-zinc-50">
            <td colSpan={editMode ? 7 : 6} className="p-1.5 text-right text-zinc-500 uppercase text-[10px] tracking-wider">
              {intra ? "CGST + SGST" : "IGST"}
            </td>
            <td className="p-1.5 text-right">₹ {t.tax.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr className="border-t border-zinc-200 bg-zinc-100 font-bold">
            <td colSpan={editMode ? 7 : 6} className="p-1.5 text-right text-zinc-700 uppercase text-[10px] tracking-wider">
              Grand Total (INR)
            </td>
            <td className="p-1.5 text-right" data-testid={`inline-grand-total-${invoiceId}`}>
              ₹ {t.total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
          </tr>
        </tfoot>
      </table>
      {editMode && (
        <div className="mt-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] border-dashed"
            onClick={addItem}
            data-testid={`inline-add-row-${invoiceId}`}
          >
            <Plus size={12} className="mr-1" /> Add Row
          </Button>
        </div>
      )}
      {editMode && (
        <div className="text-[10px] text-zinc-500 mt-2">
          Grand Total is recomputed live from the edited quantity × rate × GST %. Click <b>Save</b> to persist.
        </div>
      )}
    </div>
  );
}
